import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { MyGroupEntry, Role, UserPublic, UserRole } from '@pta/types';
import type { GroupLocale } from '@pta/types/group-locale';
import { fetchMe, fetchMyGroups } from '../api/me';
import { fetchGroupLocale } from '../api/group-locale';

/**
 * A FE elsődleges (aktív) role-ja — a layout választás ezen alapul.
 *
 * Csoport-szintű operatív role-ok (sidebar layout):
 *   pta_cashier, pta_leader
 *
 * Szülői/diák nézet (top-nav mobil-first):
 *   parent, student
 *
 * Read-only felügyeleti (most parent layout-tal megy, a fejlécben jelezzük):
 *   institution_admin, country_admin, applicationadmin, systemadmin
 */
export type ActiveRole = Role;

export interface AuthState {
  loading: boolean;
  user: UserPublic | null;
  roles: UserRole[];
  /**
   * Az aktív role, amire a layout reagál. Default: a legoperatívabb role,
   * amivel a user rendelkezik (preferenciaszint: pta_financial > pta_leader > parent > student > többi).
   * A user válthat (header dropdown).
   */
  activeRole: ActiveRole | null;
  setActiveRole: (role: ActiveRole) => void;

  /** A user csoportjai (pta_leader / pta_cashier / felsőbb admin scope alapján). */
  myGroups: MyGroupEntry[];
  /** Az aktív csoport id-ja (financial layout-on a header-picker, oldalak kontextusa). */
  activeGroupId: number | null;
  setActiveGroupId: (id: number | null) => void;

  /**
   * Az aktív csoport feloldott lokalizációs konfigurációja
   * (pénzformázás, dátumformátum, country). `null` amíg betölt vagy ha nincs aktív csoport.
   */
  activeGroupLocale: GroupLocale | null;

  refresh: () => Promise<void>;
}

const ROLE_PREFERENCE: readonly Role[] = [
  'pta_cashier',
  'pta_leader',
  'parent',
  'student',
  'institution_admin',
  'country_admin',
  'applicationadmin',
  'systemadmin',
  'b2b_supplier',
];

function pickDefaultRole(roles: UserRole[]): ActiveRole | null {
  const owned = new Set(roles.map((r) => r.role));
  for (const r of ROLE_PREFERENCE) {
    if (owned.has(r)) return r;
  }
  return roles[0]?.role ?? null;
}

const ACTIVE_GROUP_KEY = 'pta.activeGroupId';

function readPersistedGroupId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(ACTIVE_GROUP_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function writePersistedGroupId(id: number | null): void {
  if (typeof window === 'undefined') return;
  if (id === null) window.localStorage.removeItem(ACTIVE_GROUP_KEY);
  else window.localStorage.setItem(ACTIVE_GROUP_KEY, String(id));
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserPublic | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRoleState] = useState<ActiveRole | null>(null);
  const [myGroups, setMyGroups] = useState<MyGroupEntry[]>([]);
  const [activeGroupId, setActiveGroupIdState] = useState<number | null>(null);
  const [activeGroupLocale, setActiveGroupLocale] = useState<GroupLocale | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await fetchMe();
      if (!me) {
        setUser(null);
        setRoles([]);
        setActiveRoleState(null);
        setMyGroups([]);
        setActiveGroupIdState(null);
        setActiveGroupLocale(null);
        return;
      }
      setUser(me.user);
      setRoles(me.roles);
      setActiveRoleState((prev) => prev ?? pickDefaultRole(me.roles));

      // Csoportok lekérése + aktív csoport feloldása.
      const groups = await fetchMyGroups();
      setMyGroups(groups);

      const persisted = readPersistedGroupId();
      const stillValid = persisted !== null && groups.some((g) => g.id === persisted);
      const next = stillValid ? persisted : (groups[0]?.id ?? null);
      setActiveGroupIdState(next);
      writePersistedGroupId(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Aktív csoport váltásakor (vagy első betöltésnél) töltjük be a locale-t.
  // Külön effect, hogy a csoport-váltás ne kérjen újra mindent.
  useEffect(() => {
    if (activeGroupId === null) {
      setActiveGroupLocale(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const locale = await fetchGroupLocale(activeGroupId);
        if (!cancelled) setActiveGroupLocale(locale);
      } catch {
        // Network hiba esetén null marad — komponensek default-tal mennek.
        if (!cancelled) setActiveGroupLocale(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId]);

  const setActiveRole = useCallback((role: ActiveRole) => {
    setActiveRoleState(role);
  }, []);

  const setActiveGroupId = useCallback((id: number | null) => {
    setActiveGroupIdState(id);
    writePersistedGroupId(id);
  }, []);

  const value: AuthState = {
    loading,
    user,
    roles,
    activeRole,
    setActiveRole,
    myGroups,
    activeGroupId,
    setActiveGroupId,
    activeGroupLocale,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** A user role-jainak egyedi listája (set). */
export function uniqueRoles(roles: UserRole[]): Role[] {
  return Array.from(new Set(roles.map((r) => r.role)));
}

/** Sidebar (operatív) layout-ot kapó role-ok. */
export function isFinancialLayoutRole(role: ActiveRole | null): boolean {
  return role === 'pta_cashier' || role === 'pta_leader';
}
